import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

interface Patient {
  id: string;
  name: string;
  cellphone: string | null;
  last_appointment: string | null; // ISO date string or null
  next_appointment: string | null; // ISO date string or null
  completed_appointments: string | null; // ISO date string or null
}

export interface PatientsApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Patient[];
}

const formatLastAppointment = (isoDateString: string | null): string => {
  if (!isoDateString) return "Sin cita";
  try {
    const d = new Date(isoDateString);
    // If invalid date
    if (Number.isNaN(d.getTime())) return "Fecha inválida";

    // If same day -> show "Hoy, 4:00 PM" style
    const now = new Date();
    const isToday =
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear();

    if (isToday) {
      return `Hoy, ${d.toLocaleTimeString("es-ES", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })}`;
    }

    // Otherwise show date and time
    return `${d.toLocaleDateString("es-ES")}, ${d.toLocaleTimeString("es-ES", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;
  } catch (err) {
    return "Nunca";
  }
};

interface RegisteredPatientsTableProps {
  data: PatientsApiResponse;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onNewPatientClick: () => void;
  onViewPatient: (id: string) => void;
  onEditPatient: (id: string) => void;
}

export const RegisteredPatientsTable: React.FC<
  RegisteredPatientsTableProps
> = ({
  data,
  onNextPage,
  onPreviousPage,
  onNewPatientClick,
  onViewPatient,
  onEditPatient,
}) => {
  const patients = data.results ?? [];
  const totalCount = data?.count ?? 0;
  const hasNextPage = !!data?.next;
  const hasPreviousPage = !!data?.previous;
  const navigate = useNavigate();

  const currentRange = useMemo(() => {
    if (patients.length === 0) return `Mostrando 0 de ${totalCount} pacientes`;
    return `Mostrando 1-${patients.length} de ${totalCount} pacientes`;
  }, [patients.length, totalCount]);

  const handleViewPatient = (patientId: string) => {
    navigate(`/patient/${patientId}`);
  };
  const handleEditPatient = (patientId: string) => {
    navigate(`/patient/edit/${patientId}`);
  };

  // if +52 in phone, remove
  function getLocalNumberOnly(phoneNumber: string | null): string | null {
    if (phoneNumber == null) {
      return null;
    }

    const validCodes = ["+1", "+52", "+44", "+91"];

    // 1. Find which country code the phone number starts with
    const matchedCode = validCodes.find((code) => phoneNumber.startsWith(code));

    if (matchedCode) {
      // 2. Remove the matched code from the string
      // .slice(matchedCode.length) starts the string AFTER the country code
      return phoneNumber.slice(matchedCode.length);
    }

    // Return null or the original number if no valid country code is found
    return null;
  }

  return (
    <div
      className="container agenda-content full-width-main"
      id="main-content-patients"
    >
      <div className="main-calendar-view">
        <div className="main-calendar-header">
          <h2 id="view-title">Pacientes</h2>
          <div>
            <button className="btn btn--primary" onClick={onNewPatientClick}>
              <i className="fas fa-user-plus"></i> Nuevo Paciente
            </button>
          </div>
        </div>

        <div id="data-table-view" className="data-table-container">
          <div className="table-controls">
            <input
              type="text"
              placeholder="Buscar por nombre o teléfono..."
              className="search-input"
            />
          </div>

          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nombre completo</th>
                  <th>Teléfono</th>
                  <th>Última cita</th>
                  <th>Próxima cita</th>
                  <th>Citas completadas</th>
                  <th className="actions-column">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {patients.map((p) => (
                  <tr key={p.id}>
                    <td data-label="Nombre completo">{p.name}</td>
                    <td data-label="Teléfono">
                      {getLocalNumberOnly(p.cellphone) ?? "-"}
                    </td>
                    <td data-label="Última cita">
                      {formatLastAppointment(p.last_appointment)}
                    </td>
                    <td data-label="Próxima cita">
                      {formatLastAppointment(p.next_appointment)}
                    </td>
                    <td className="number" data-label="Citas completadas">
                      {p.completed_appointments}
                    </td>
                    <td data-label="Acciones" className="actions-column">
                      <button
                        className="btn btn--secondary btn--small"
                        title="Ver"
                        onClick={() => handleViewPatient(p.id)}
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      <button
                        className="btn btn--secondary btn--small"
                        title="Editar"
                        onClick={() => handleEditPatient(p.id)}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                    </td>
                  </tr>
                ))}

                {patients.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No se encontraron pacientes.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <span>{currentRange}</span>
            <button
              className="btn btn--secondary btn--small"
              disabled={!hasPreviousPage}
              onClick={onPreviousPage}
            >
              <i className="fas fa-chevron-left"></i> Anterior
            </button>
            <button
              className="btn btn--secondary btn--small"
              disabled={!hasNextPage}
              onClick={onNextPage}
            >
              Siguiente <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
